import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { SEOHead } from '@/components/SEOHead';
import { ServiceLeadForm } from '@/components/ServiceLeadForm';
import { FiscalBanner } from '@/components/FiscalBanner';
import { FiscalCountdown } from '@/components/FiscalCountdown';
import { FiscalSimulator } from '@/components/FiscalSimulator';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Home, ArrowRight, Check, X, Star, Phone, 
  AlertTriangle, TrendingDown, Calculator, Building2
} from 'lucide-react';

// Images rénovation complète
const HERO_IMAGE = "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=1600&auto=format&fit=crop";
const CONTENT_IMAGE = "https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?w=800&auto=format&fit=crop";

export const RenovationCompletePage = () => {
  const formRef = useRef(null);
  const simulatorRef = useRef(null);

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToSimulator = () => {
    simulatorRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const questions = [
    { id: 'type_bien', label: 'Type de bien', type: 'select', options: ['Appartement', 'Maison', 'Chalet', 'Duplex', 'Loft'] },
    { id: 'surface', label: 'Surface totale', type: 'text', placeholder: 'Ex: 120 m²' },
    { id: 'localisation', label: 'Localisation', type: 'text', placeholder: 'Ex: Lausanne, Genève...' },
    { id: 'travaux', label: 'Travaux envisagés', type: 'select', options: ['Tout refaire', 'Pièces principales', 'Sols + peinture', 'Électricité + plomberie', 'Autre'] },
    { id: 'budget', label: 'Budget estimatif', type: 'text', placeholder: 'Ex: 80\'000 CHF' },
    { id: 'optimisation_fiscale', label: 'Optimisation fiscale ?', type: 'select', options: ['Oui, c\'est prioritaire', 'J\'aimerais en savoir plus', 'Non'] }
  ];

  // Exemples rénovation complète
  const completeExamples = [
    { type: 'Appartement 3 pièces', budget: 60000, surface: '70 m²', description: 'Rafraîchissement complet' },
    { type: 'Appartement 5 pièces', budget: 100000, surface: '120 m²', description: 'Rénovation intégrale' },
    { type: 'Maison individuelle', budget: 180000, surface: '200 m²', description: 'Transformation totale' }
  ];

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Rénovation complète d'appartement et maison en Suisse romande",
    "provider": {
      "@type": "LocalBusiness",
      "name": "Homely Solutions"
    },
    "description": "Rénovation complète avec optimisation fiscale avant la réforme 2028. Cuisine, salle de bain, sols, peinture, électricité."
  };

  return (
    <div className="min-h-screen bg-background" data-testid="renovation-complete-page">
      <SEOHead 
        title="Rénovation Complète Suisse | Économisez des milliers de CHF avant 2028 | Homely"
        description="Rénovation complète d'appartement ou maison avec optimisation fiscale. Suite à la réforme votée, ces avantages disparaîtront. Projet clé en main."
        keywords="rénovation complète suisse, rénovation appartement lausanne, rénovation maison genève, déduction fiscale rénovation"
        jsonLd={jsonLd}
      />

      {/* Bannière fiscale urgente */}
      <FiscalBanner onSimulatorClick={scrollToSimulator} />
      
      <Header />
      
      {/* ============================================
          HERO - ANGLE FISCAL RÉNOVATION COMPLÈTE
          ============================================ */}
      <section className="hero-section relative min-h-[70vh] sm:min-h-[80vh] md:min-h-[85vh] flex items-center">
        <img 
          src={HERO_IMAGE} 
          alt="Appartement rénové moderne" 
          className="hero-image-bg"
        />
        <div className="mx-auto w-full max-w-[var(--container)] px-4 sm:px-6 lg:px-8 py-10 sm:py-16 md:py-20">
          <div className="max-w-3xl">
            <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
              <Home className="h-7 w-7 sm:h-8 sm:w-8 md:h-10 md:w-10 text-white" />
              <Badge className="bg-orange-500 text-white border-orange-400 text-xs sm:text-sm">
                Fenêtre fiscale limitée
              </Badge>
            </div>
            
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold mb-4 sm:mb-6 text-white leading-tight" style={{ fontFamily: 'var(--font-heading)' }}>
              Rénovation complète :<br />
              <span className="text-orange-300">20'000 CHF d'économie</span><br />
              si vous agissez maintenant.
            </h1>
            
            <p className="text-sm sm:text-base md:text-lg lg:text-xl text-white/90 mb-3 sm:mb-4 max-w-2xl">
              Un projet à 80'000 CHF peut vous revenir à <strong>60'000 CHF</strong> grâce aux déductions fiscales actuelles.
            </p>
            <p className="text-sm sm:text-base md:text-lg lg:text-xl text-white font-semibold mb-5 sm:mb-8 max-w-2xl">
              Après la réforme ? Vous paierez <span className="text-orange-300">80'000 CHF</span>. Sans discussion.
            </p>

            {/* Compte à rebours */}
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 sm:p-4 mb-5 sm:mb-8 inline-block">
              <FiscalCountdown />
            </div>
            
            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <Button 
                size="lg" 
                className="h-12 sm:h-14 px-6 sm:px-10 text-sm sm:text-base rounded-full bg-orange-500 hover:bg-orange-600 w-full sm:w-auto"
                onClick={scrollToSimulator}
                data-testid="complete-cta-simulator"
              >
                <Calculator className="mr-2 h-4 w-4 sm:h-5 sm:w-5" />
                Simuler mon économie
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                className="h-12 sm:h-14 px-6 sm:px-8 text-sm sm:text-base rounded-full bg-white/10 border-white/30 text-white hover:bg-white hover:text-[var(--anthracite)] backdrop-blur-sm w-full sm:w-auto"
                onClick={scrollToForm}
              >
                <TrendingDown className="mr-2 h-4 w-4 sm:h-5 sm:w-5" />
                Demander un devis
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================
          SECTION PROBLÈME - RÉNOVATION COMPLÈTE
          ============================================ */}
      <section className="py-12 sm:py-16 md:py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="mx-auto w-full max-w-[var(--container)] px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-12">
            <Badge className="mb-4 bg-red-100 text-red-700">Le constat</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
              Plus votre projet est grand, plus vous perdez en attendant.
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Une rénovation complète représente souvent des dizaines de milliers de francs.
              L'avantage fiscal est proportionnel : ne le laissez pas s'envoler.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="bg-green-50 border-2 border-green-200 rounded-2xl p-8">
              <h3 className="text-xl font-bold text-green-800 mb-6 flex items-center gap-2">
                <div className="w-4 h-4 bg-green-500 rounded-full"></div>
                Rénover maintenant
              </h3>
              <ul className="space-y-4">
                {[
                  'Déduction fiscale maximale',
                  'Économie pouvant dépasser 20\'000 CHF',
                  'Un bien transformé + impôts réduits'
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <Check className="h-6 w-6 text-green-600 flex-shrink-0" />
                    <span className="text-green-900">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-8">
              <h3 className="text-xl font-bold text-red-800 mb-6 flex items-center gap-2">
                <div className="w-4 h-4 bg-red-500 rounded-full"></div>
                Attendre la réforme
              </h3>
              <ul className="space-y-4">
                {[
                  'Plus aucune déduction',
                  'Des dizaines de milliers perdus',
                  'Même résultat, coût supérieur'
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <X className="h-6 w-6 text-red-600 flex-shrink-0" />
                    <span className="text-red-900">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="text-center mt-12 p-6 bg-orange-100 rounded-xl max-w-2xl mx-auto">
            <Building2 className="h-8 w-8 text-orange-600 mx-auto mb-3" />
            <p className="text-xl font-bold text-orange-800">
              Rénovation à 80'000 CHF = <span className="line-through">80'000</span> → <span className="text-green-700">60'000 CHF</span> aujourd'hui
            </p>
            <p className="text-orange-700 mt-2">
              Économie potentielle : <strong>20'000 CHF</strong>
            </p>
          </div>
        </div>
      </section>

      {/* ============================================
          SIMULATEUR FISCAL
          ============================================ */}
      <section className="py-20 bg-gray-50" ref={simulatorRef}>
        <div className="mx-auto w-full max-w-[var(--container)] px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div>
              <Badge className="mb-4 bg-orange-100 text-orange-700">Simulation rénovation complète</Badge>
              <h2 className="text-3xl sm:text-4xl font-bold mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
                Combien coûte réellement votre rénovation ?
              </h2>
              <p className="text-lg text-muted-foreground mb-8">
                Appartement, maison, chalet... Plus le projet est conséquent,
                plus l'économie fiscale est importante. Calculez maintenant.
              </p>

              <FiscalCountdown />
            </div>

            <FiscalSimulator onCtaClick={scrollToForm} />
          </div>
        </div>
      </section>

      {/* ============================================
          EXEMPLES CONCRETS RÉNOVATION COMPLÈTE
          ============================================ */}
      <section className="py-20">
        <div className="mx-auto w-full max-w-[var(--container)] px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
              Exemples de rénovations complètes
            </h2>
            <p className="text-lg text-muted-foreground">
              L'économie fiscale croît avec l'ampleur du projet
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {completeExamples.map((example, idx) => {
              const economie = Math.round(example.budget * 0.25);
              const coutReel = example.budget - economie;
              
              return (
                <div key={idx} className="bg-white rounded-2xl shadow-lg overflow-hidden border">
                  <div className="p-6">
                    <h3 className="text-xl font-bold mb-1">{example.type}</h3>
                    <p className="text-sm text-muted-foreground mb-4">{example.surface} • {example.description}</p>
                    
                    <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Budget</span>
                        <span className="font-semibold">{example.budget.toLocaleString('fr-CH')} CHF</span>
                      </div>
                      
                      <div className="border-t pt-3">
                        <div className="flex justify-between text-green-600">
                          <span>Économie fiscale</span>
                          <span className="font-semibold">-{economie.toLocaleString('fr-CH')} CHF</span>
                        </div>
                        <div className="flex justify-between text-green-700 font-bold mt-1">
                          <span>Coût réel aujourd'hui</span>
                          <span>{coutReel.toLocaleString('fr-CH')} CHF</span>
                        </div>
                      </div>
                      
                      <div className="border-t pt-3">
                        <div className="flex justify-between text-red-600">
                          <span>Après réforme</span>
                          <span className="font-bold">{example.budget.toLocaleString('fr-CH')} CHF</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="mt-4 p-3 bg-orange-100 rounded-lg text-center">
                      <span className="text-orange-800 font-bold">
                        Perte si attente : +{economie.toLocaleString('fr-CH')} CHF
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============================================
          CE QUE COMPREND UNE RÉNOVATION COMPLÈTE
          ============================================ */}
      <section className="py-20 gradient-premium">
        <div className="mx-auto w-full max-w-[var(--container)] px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="mb-4" style={{ backgroundColor: 'var(--sage)', color: 'white' }}>Projet clé en main</Badge>
              <h2 className="text-3xl sm:text-4xl font-bold mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
                Une rénovation complète,<br />un interlocuteur unique
              </h2>
              
              <p className="text-lg text-muted-foreground mb-6">
                Nous coordonnons l'ensemble des corps de métier pour transformer votre bien 
                tout en optimisant votre situation fiscale.
              </p>

              <div className="grid grid-cols-2 gap-4 mb-8">
                {[
                  'Cuisine',
                  'Salle(s) de bain',
                  'Sols (parquet, carrelage)',
                  'Peinture complète',
                  'Électricité aux normes',
                  'Plomberie',
                  'Menuiseries',
                  'Isolation'
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Check className="h-5 w-5 text-[var(--sage)]" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>

              <Button 
                size="lg" 
                className="h-14 px-10 rounded-full bg-orange-500 hover:bg-orange-600"
                onClick={scrollToForm}
              >
                Demander un devis complet
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>

            <div className="relative">
              <img 
                src={CONTENT_IMAGE} 
                alt="Intérieur rénové moderne" 
                className="rounded-2xl shadow-2xl w-full"
              />
              <div className="absolute -bottom-6 -left-6 bg-gradient-to-r from-orange-500 to-red-500 text-white p-6 rounded-xl shadow-lg">
                <AlertTriangle className="h-8 w-8 mb-2" />
                <p className="font-bold">Fenêtre fiscale</p>
                <p className="text-sm text-white/80">Jusqu'à 45'000 CHF d'économie</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================
          TÉMOIGNAGES
          ============================================ */}
      <section className="py-20">
        <div className="mx-auto w-full max-w-[var(--container)] px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-green-100 text-green-700">Témoignages</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
              Ils ont transformé leur bien avant la réforme
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {[
              { 
                name: 'Famille Müller', 
                city: 'Genève', 
                budget: '95\'000 CHF',
                economie: '23\'750 CHF',
                text: 'Rénovation complète de notre appartement 5 pièces. L\'économie fiscale a financé toute la cuisine équipée et une partie de la salle de bain !'
              },
              { 
                name: 'Jean-Marc & Claire S.', 
                city: 'Vevey', 
                budget: '140\'000 CHF',
                economie: '35\'000 CHF',
                text: 'Notre maison des années 70 est méconnaissable. 35\'000 CHF économisés, c\'est l\'équivalent de deux belles voitures. On n\'aurait jamais attendu !'
              }
            ].map((review, idx) => (
              <div key={idx} className="card-premium p-6">
                <div className="flex gap-1 mb-3">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-muted-foreground mb-4 italic">"{review.text}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-[var(--sage)] flex items-center justify-center text-white font-bold text-lg">
                    {review.name.charAt(0)}
                  </div>
                  <div>
                    <p className="font-semibold">{review.name}</p>
                    <p className="text-sm text-muted-foreground">{review.city}</p>
                  </div>
                </div>
                <div className="mt-4 pt-3 border-t flex justify-between text-sm">
                  <span className="text-muted-foreground">Budget : {review.budget}</span>
                  <span className="text-green-600 font-semibold">Économie : {review.economie}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================
          LIEN VERS AUTRES RÉNOVATIONS
          ============================================ */}
      <section className="py-12 bg-gray-50">
        <div className="mx-auto w-full max-w-[var(--container)] px-4 sm:px-6 lg:px-8">
          <p className="text-center text-muted-foreground mb-6">Projets de rénovation spécifiques :</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/renovation/cuisine" className="px-6 py-3 bg-white border rounded-full hover:bg-[var(--sage)] hover:text-white transition-colors">
              Cuisine
            </Link>
            <Link to="/renovation/salle-de-bain" className="px-6 py-3 bg-white border rounded-full hover:bg-[var(--sage)] hover:text-white transition-colors">
              Salle de bain
            </Link>
            <Link to="/renovation" className="px-6 py-3 bg-white border rounded-full hover:bg-[var(--sage)] hover:text-white transition-colors">
              Tous nos services rénovation
            </Link>
          </div>
        </div>
      </section>

      {/* ============================================
          CTA FINAL
          ============================================ */}
      <section className="py-20" style={{ backgroundColor: 'var(--anthracite)' }} ref={formRef}>
        <div className="mx-auto w-full max-w-[var(--container)] px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="text-white">
              <Badge className="mb-4 bg-orange-500">Devis gratuit rénovation complète</Badge>
              <h2 className="text-3xl sm:text-4xl font-bold mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
                Transformez votre bien,<br />optimisez vos impôts.
              </h2>
              <p className="text-white/70 mb-8 text-lg">
                Décrivez votre projet et recevez une estimation globale incluant 
                le calcul de votre économie fiscale potentielle.
              </p>

              <div className="space-y-4 mb-8">
                {[
                  'Estimation économie fiscale incluse',
                  'Devis détaillé sous 48h',
                  'Visite gratuite sur place',
                  'Coordination tous corps de métier',
                  'Sans engagement'
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-orange-500 flex items-center justify-center">
                      <Check className="h-4 w-4 text-white" />
                    </div>
                    <span className="text-white/90">{item}</span>
                  </div>
                ))}
              </div>

              <div className="mb-8">
                <FiscalCountdown />
              </div>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-2xl">
              <div className="mb-6 p-4 bg-orange-50 rounded-lg border border-orange-200">
                <p className="text-sm text-orange-800 font-medium flex items-center gap-2">
                  <Home className="h-5 w-5" />
                  Projet rénovation complète - Optimisation fiscale
                </p>
              </div>
              <ServiceLeadForm service="renovation-complete" pole="renovation" questions={questions} />
            </div>
          </div>
        </div>
      </section>

      {/* Footer fiscal */}
      <section className="py-6 bg-gray-100 border-t">
        <div className="mx-auto w-full max-w-[var(--container)] px-4 sm:px-6 lg:px-8">
          <p className="text-sm text-center text-muted-foreground">
            Les règles fiscales évoluent suite à la réforme votée en Suisse en 2025.
            Les déductions actuelles restent applicables pendant la phase transitoire.
            Les estimations présentées sont indicatives (taux ~25%) et varient selon votre situation fiscale.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
};
